import { Controller } from "@hotwired/stimulus";
import { navigator } from "@hotwired/turbo";
import {
  clear,
  load,
  promote,
  remove,
  rowFragment,
  type RecentSearch,
} from "../lib/recent-search";

/**
 * Search as you type and remember what was chosen, which is what `SearchViewModel` does on iOS.
 *
 * The screen was a GET form and nothing else: you typed, you pressed Enter, the page reloaded.
 * That is still what happens with no script, and it is still the address a result is shared
 * under. What this adds is the app's own behaviour on top of it - the list follows the field, and
 * the screen you land on is the last people you looked up rather than an empty invitation.
 *
 * The results are a Turbo frame pointed back at this very page rather than at a fragment
 * endpoint of its own. The live path and the no-script path then render from the same controller
 * and the same template, so the two cannot drift: there is no second view of a search result to
 * keep in step. The layout around the frame is rebuilt and thrown away on each pass, which costs
 * a Thymeleaf render and no extra query, because the search itself is the only thing on this
 * page that touches the database.
 *
 * The recent searches are the other way round: they are the reader's own, they are held in their
 * own browser by `lib/recent-search`, and the server is never told about them or asked to render
 * them. Which of the two lists is on screen is this controller's single rule, below.
 */

/* iOS's numbers, from `SearchViewModel`: 500ms after the typing stops, and nothing is asked for
   under three characters. Constants rather than Stimulus values because there is one search
   screen and these are the app's rules, not this page's settings. */
const DEBOUNCE_MS = 500;
const MINIMUM_LENGTH = 3;

export default class extends Controller<HTMLElement> {
  static override targets = [
    "form",
    "frame",
    "recent",
    "recentList",
    "recentRow",
  ];

  declare readonly formTarget: HTMLFormElement;
  declare readonly frameTarget: HTMLElement;
  declare readonly recentTarget: HTMLElement;
  declare readonly recentListTarget: HTMLElement;
  declare readonly recentRowTarget: HTMLTemplateElement;

  private pending: ReturnType<typeof setTimeout> | undefined;

  /** The address the frame already shows, so typing and deleting a character asks for nothing. */
  private loaded = "";

  override connect(): void {
    this.loaded = this.address();
    this.draw(load());
  }

  override disconnect(): void {
    clearTimeout(this.pending);
  }

  /*
   * The busy flag goes on here, on the keystroke, and not when the request starts.
   *
   * That is `SearchViewModel` setting `isLoading` in `queryChanged()` rather than in
   * `performSearch`, and for the same reason: for the next half second the page is showing an
   * answer to a question that is no longer being asked, and nothing would say so.
   *
   * Turbo marks and clears the frame busy around its own fetch, so it takes the flag over from
   * here and removes it once the frame has rendered - including when the request failed.
   *
   * The swap between the two lists is not debounced, though. `SearchViewModel` switches on the
   * character that takes the query over the minimum, not half a second after it, and a screen
   * still showing yesterday's searches while today's are being typed reads as a field that has
   * stopped listening.
   */
  onInput(): void {
    clearTimeout(this.pending);
    this.frameTarget.setAttribute("aria-busy", "true");
    this.reveal();
    this.pending = setTimeout(() => this.search(), DEBOUNCE_MS);
  }

  /** Enter is somebody saying they have finished typing, so it does not wait out the debounce. */
  onSubmit(event: Event): void {
    event.preventDefault();
    clearTimeout(this.pending);
    this.reveal();
    this.search();
  }

  /**
   * Remembers whichever row was chosen, live result or recent search alike.
   *
   * `SearchViewModel.selectResult` is called from both lists for the same reason: choosing a
   * recent search is still choosing it, and it belongs back at the top afterwards.
   *
   * One delegated handler on the controller's own element rather than a `data-action` per row.
   * The result rows are written by the server into a frame it replaces wholesale, and the recent
   * rows are written here; a listener above both is the only place that covers them without the
   * row markup having to know which of the two lists it is in.
   *
   * Nothing is drawn afterwards. The click is a whole-page navigation - the frame is
   * `target="_top"` - so this page is on its way out, and `localStorage` is written
   * synchronously, which is what makes that safe.
   */
  onSelect(event: Event): void {
    const target = event.target;
    if (!(target instanceof Element)) {
      return;
    }

    const row = target.closest<HTMLAnchorElement>("a[data-search-result-id]");
    if (row === null) {
      return;
    }

    const id = row.dataset.searchResultId;
    const url = row.getAttribute("href");
    if (id === undefined || url === null) {
      return;
    }

    promote({ id, row: row.innerHTML, url });
  }

  /**
   * One recent search, which is iOS's swipe-to-delete without the swipe.
   *
   * Delegated from the controller's element for the same reason `onSelect` is, and not declared
   * on the button in the template: an action attribute only binds once Stimulus has seen the
   * element, and these rows are written by the controller itself. A listener above them is
   * already connected when they arrive.
   */
  removeRecent(event: Event): void {
    const target = event.target;
    if (
      !(target instanceof Element) ||
      target.closest("[data-search-recent-remove]") === null
    ) {
      return;
    }

    const id = target.closest<HTMLElement>("[data-search-recent-id]")?.dataset
      .searchRecentId;
    if (id === undefined) {
      return;
    }

    this.draw(remove(id));
  }

  /**
   * All of them, which the sheet over this page has already asked about.
   *
   * The confirmation is the app's own: `SearchView` puts an alert in front of this, because the
   * list cannot be got back and a mis-aimed tap on a header button is an easy way to lose it.
   * The sheet closes itself through the component library's `data-wp-sheet-close`, so there is
   * nothing here about dismissing it.
   */
  clearRecent(): void {
    this.draw(clear());
  }

  private search(): void {
    /* A pending search belongs to the page it was typed on, and a Turbo visit can take that page
           away while the debounce is still running. `disconnect()` is what cancels it; this is what
           makes that true even when the cancellation has not reached us yet, which is the one order
           of events that would otherwise point a frame nobody is looking at at a new address. */
    if (!this.element.isConnected) {
      return;
    }

    const address = this.address();

    /* Nothing to ask for: the query came back to what is already on screen, which is what
           deleting the character you just typed does. Turbo never starts, so the flag it would
           have cleared has to come off here. */
    if (address === this.loaded) {
      this.frameTarget.removeAttribute("aria-busy");

      return;
    }

    this.loaded = address;
    this.frameTarget.setAttribute("src", address);

    /* Replace rather than push: a result is a URL somebody can keep or share, which is the
           whole reason the field is a GET form, but one history entry per keystroke would make
           the back button walk backwards through the typing. Turbo's own history rather than
           `history.replaceState`, so the session's idea of where it is stays true. */
    navigator.history.replace(new URL(address, window.location.href));
  }

  /**
   * Draws the recent searches and then decides which list the screen is showing.
   *
   * The rows are built from the template the server put on the page rather than from markup
   * written here, so the remove button's glyph and its label come out of the component library
   * and the app's own resource bundle like everything else on the screen does.
   */
  private draw(entries: readonly RecentSearch[]): void {
    this.recentListTarget.replaceChildren();

    entries.forEach((entry, index) => {
      const row = this.recentRowTarget.content.cloneNode(
        true,
      ) as DocumentFragment;

      const wrapper = row.querySelector<HTMLElement>(".wp-list-row");
      const link = row.querySelector<HTMLAnchorElement>("a");
      if (wrapper === null || link === null) {
        return;
      }

      wrapper.dataset.searchRecentId = entry.id;
      link.dataset.searchResultId = entry.id;
      link.href = entry.url;
      link.append(rowFragment(entry.row));

      /* The stored row carries the number it had among the results it was chosen from, which
               was a fact about that search and not about this list. `SearchRowView` is passed its
               row's position either way, so the number is restated here rather than kept. */
      const position = link.querySelector(".wp-position");
      if (position !== null) {
        position.textContent = String(index + 1);
      }

      this.recentListTarget.append(row);
    });

    this.reveal(entries.length);
  }

  /**
   * Which of the two lists is on screen.
   *
   * `SearchView` shows the recent searches below the minimum and the results above it, and falls
   * back to the invitation when there is nothing recent to show. That is one rule with three
   * outcomes, and the frame is hidden rather than emptied for the third: the invitation and the
   * "nothing matched" state are both inside it, already rendered, and whichever of them is in
   * there is the right thing to show the moment the recent list empties.
   */
  private reveal(count = this.recentListTarget.childElementCount): void {
    const recent = count > 0 && this.query().length < MINIMUM_LENGTH;

    /* Each only when it is actually changing. Every keystroke calls this, and writing an
           attribute the element already has would be a DOM mutation per character for nothing. */
    if (this.recentTarget.hidden !== !recent) {
      this.recentTarget.hidden = !recent;
    }

    if (this.frameTarget.hidden !== recent) {
      this.frameTarget.hidden = recent;
    }
  }

  /**
   * Where the results for whatever is in the field right now live.
   *
   * Built from the form's own action and the field's own name, so the controller keeps no copy
   * of either. Under the minimum the query is left off altogether, which asks for the bare
   * page: on iOS a query that short clears the results and hands the screen back to the recent
   * searches, and this does both of those too.
   *
   * Dropping everything but the path and the query also resets the paging, which is right -
   * page four of the last search is not page four of this one.
   */
  private address(): string {
    const url = new URL(this.formTarget.action, window.location.href);
    const field = this.field();
    const query = this.query();

    if (query.length >= MINIMUM_LENGTH) {
      url.searchParams.set(field.name, query);
    }

    return `${url.pathname}${url.search}`;
  }

  private query(): string {
    return this.field().value.trim();
  }

  /*
   * The field is found rather than declared as a target: it is drawn by the component library's
   * `search` fragment, which takes the handful of parameters it takes and knows nothing about
   * Stimulus. Giving it a `data-search-target` would mean teaching a design-system fragment
   * about this application's controllers.
   */
  private field(): HTMLInputElement {
    const field = this.formTarget.querySelector<HTMLInputElement>(
      'input[type="search"]',
    );
    if (field === null) {
      throw new Error("the search form has no search field in it");
    }

    return field;
  }
}
